import {
    DataObjectType,
    IColumnText,
    ILanguageTextChangeEvent,
    ILanguageTextService,
    ILanguageTextServiceContext,
    IMessageText
} from "../PA.Core.IF.Language";
import {IDataObjectCacheEntry, LanguageTextService} from "./LanguageTextService";
import {IMessageProvider, IProgressProvider} from "../PA.Core.IF.Basic";
import {BehaviorSubject, Subscription} from "rxjs";
import {MessageProvider} from "../Basic/Messages";
import {ProgressProvider} from "../Basic/Progress";
import * as _ from "lodash"
import dataDrivenParameter from "../../../client-ui5/PA-TableauExt/dynamicparam/src/DataDrivenParameter";

export interface IAliasEntry {
    aliasId: string,
    dataObjectTypeId: string,
    dataObjectId: string
}



export class LanguageTextServiceContext
    implements ILanguageTextServiceContext
{
    private _lts:  LanguageTextService
    private _loadMessageProvider: MessageProvider
    private _loadProgressProvider: ProgressProvider
    private _onLanguageTextChanged: BehaviorSubject<ILanguageTextChangeEvent>;
    private _dctAlias: {[aliasId: string]: IAliasEntry}
    private _sLanguageCdPrimary: string
    private _sLanguageCdSecondary: string[]

    private _dctDORefs: {[dataObjectTypeId:string]: {[dataObjectId: string]: boolean}}
    private _sub: Subscription = undefined

    constructor(lts: LanguageTextService, languageCd: string | string[]) {
        const self = this

        this._lts = lts
        this._loadMessageProvider = new MessageProvider()
        this._loadProgressProvider = new ProgressProvider("LanguageTextServiceContext")
        this._onLanguageTextChanged = new BehaviorSubject<ILanguageTextChangeEvent>(undefined)
        this._dctAlias = {}
        this._dctDORefs = {}
        this._sLanguageCdPrimary = "en"
        this._sLanguageCdSecondary = []

        if(_.isString(languageCd)){
            this._sLanguageCdPrimary = languageCd
            this._sLanguageCdSecondary = []
        } else if(_.isArray(languageCd) && languageCd.length){
            this._sLanguageCdPrimary = _.first(languageCd)
            this._sLanguageCdSecondary = languageCd.splice(1)
        }

        this._sub = LanguageTextService.onDataChanged.subscribe((evt) => {
            if(_.isEmpty(evt))
                return

            if(!_.includes(evt.Languages, self._sLanguageCdPrimary))
                return;

            // Check if any of the aliases are in the change set
            let arrChangedDOs: {Type:string, Id: string}[] = []
            _.forEach(evt.DataObjects, (aDO) => {
                if(self.hasReference(aDO.Type, aDO.Id)){
                    arrChangedDOs.push(aDO)
                }
            });

            if(arrChangedDOs.length)
                self.OnLanguageTextChanged.next({
                    Context: self,
                    EventType: "DataChange",
                    DataObjects: arrChangedDOs
                })
        })

    }

    destroy(){
        if(this._sub)
            this._sub.unsubscribe()
    }


    get LanguageCd(): string { return this._sLanguageCdPrimary }
    setLanguage(sLanguageCode: string): void {
        this._sLanguageCdPrimary = sLanguageCode
    }

    get LanguageTextService(): ILanguageTextService { return this._lts }

    get LoadMessagesProvider(): IMessageProvider { return this._loadMessageProvider }
    get LoadProgressProvider(): IProgressProvider { return this._loadProgressProvider }

    get OnLanguageTextChanged(): BehaviorSubject<ILanguageTextChangeEvent>{ return this._onLanguageTextChanged }


    aliasTable(aliasId: string, sTableId: string, bPreload?: boolean): void {
        this.setAlias(aliasId, DataObjectType.Table, sTableId)
        if(bPreload)
            this._lts.queueDataObjectLoad(DataObjectType.Table, sTableId, this.LanguageCd)
    }

    aliasView(aliasId: string, sViewId: string, bPreload?: boolean): void {
        this.setAlias(aliasId, DataObjectType.View, sViewId)
        if(bPreload)
            this._lts.queueDataObjectLoad(DataObjectType.View, sViewId, this.LanguageCd)
    }

    aliasUIControl(aliasId: string, sUIControlId: string, bPreload?: boolean): void {
        this.setAlias(aliasId, DataObjectType.UIControl, sUIControlId)
        if(bPreload)
            this._lts.queueDataObjectLoad(DataObjectType.UIControl, sUIControlId, this.LanguageCd)
    }

    aliasUIView(aliasId: string, sUIViewId: string, bPreload?: boolean): void {
        this.setAlias(aliasId, DataObjectType.UIView, sUIViewId)
        if(bPreload)
            this._lts.queueDataObjectLoad(DataObjectType.UIView, sUIViewId, this.LanguageCd)
    }

    aliasResource(aliasId: string, sResourceId: string, bPreload?: boolean): void {
        this.setAlias(aliasId, DataObjectType.Resource, sResourceId)
        if(bPreload)
            this._lts.queueDataObjectLoad(DataObjectType.Resource, sResourceId, this.LanguageCd)
    }

    hasAlias(aliasId: string): boolean { return aliasId in this._dctAlias; }

    private setAlias(aliasId: string, dataObjectTypeId: string, dataObjectId: string){
        if(!(aliasId in this._dctAlias)){
            this._dctAlias[aliasId] = {aliasId: aliasId, dataObjectTypeId: dataObjectTypeId, dataObjectId: dataObjectId}
        } else {
            let ae = this._dctAlias[aliasId]
            if((ae.dataObjectId != dataObjectId) && (ae.dataObjectTypeId != dataObjectTypeId)){
                console.log("Inconsistent Alias definition: ", ae, " DOT: ", dataObjectTypeId, " DO: ", dataObjectId)
            }
        }

        this.addReference(dataObjectTypeId, dataObjectId)
    }

    addReference(dataObjectTypeId: string, dataObjectId: string): void {
        if (!(dataObjectTypeId in this._dctDORefs))
            this._dctDORefs[dataObjectTypeId] = {}

        if (!(dataObjectId in this._dctDORefs[dataObjectTypeId]))
            this._dctDORefs[dataObjectTypeId][dataObjectId] = true
    }

    hasReference(dataObjectTypeId: string, dataObjectId: string): boolean {
            if(!(dataObjectTypeId in this._dctDORefs))
                return false

            return (dataObjectId in this._dctDORefs[dataObjectTypeId])
    }


    //--

    getAliasText(aliasId: string, columnId: string): IColumnText {
        if (!(aliasId in this._dctAlias))
            return undefined;

        let ae = this._dctAlias[aliasId]

        return this.getColumnText(ae.dataObjectTypeId, ae.dataObjectId, columnId)
    }

    getAliasTextAsync(aliasId: string, columnId: string): Promise<IColumnText> {
        if (!(aliasId in this._dctAlias))
            return Promise.resolve(undefined);

        let ae = this._dctAlias[aliasId]

        return this.getColumnTextAsync(ae.dataObjectTypeId, ae.dataObjectId, columnId)
    }

    getAliasMessage(aliasId: string, messageId: string): IMessageText {
        if (!(aliasId in this._dctAlias))
            return undefined;

        let ae = this._dctAlias[aliasId]

        return this.getMessage(ae.dataObjectTypeId, ae.dataObjectId, messageId)
    }

    getAliasMessageAsync(aliasId: string, messageId: string): Promise<IMessageText> {
        if (!(aliasId in this._dctAlias))
            return Promise.resolve(undefined);

        let ae = this._dctAlias[aliasId]

        return this.getMessageAsync(ae.dataObjectTypeId, ae.dataObjectId, messageId)
    }

    //--

    getColumnText(dataObjectTypeId: string, dataObjectId: string, columnId: string): IColumnText {
        return this._lts.getColumnText(dataObjectTypeId, dataObjectId, columnId, this._sLanguageCdPrimary, {languageCdSecondary: this._sLanguageCdSecondary});
    }

    getColumnTextAsync(dataObjectTypeId: string, dataObjectId: string, columnId: string): Promise<IColumnText> {
        return this._lts.getColumnTextAsync(dataObjectTypeId, dataObjectId, columnId, this._sLanguageCdPrimary, {languageCdSecondary: this._sLanguageCdSecondary});
    }

    getMessage(dataObjectTypeId: string, dataObjectId: string, messageId: string): IMessageText {
        return this._lts.getMessage(dataObjectTypeId, dataObjectId, messageId, this._sLanguageCdPrimary, {languageCdSecondary: this._sLanguageCdSecondary});
    }

    getMessageAsync(dataObjectTypeId: string, dataObjectId: string, messageId: string): Promise<IMessageText> {
        return this._lts.getMessageAsync(dataObjectTypeId, dataObjectId, messageId, this._sLanguageCdPrimary, {languageCdSecondary: this._sLanguageCdSecondary});
    }





}